import React, { useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import TopExperienceLayer from '../components/top/TopExperienceLayer';
import Footer from '../components/layout/Footer';
import { Send, RefreshCw, Star, Shield, Lock,
  Search, BookOpen, DollarSign, GraduationCap,
  ClipboardList, PenLine, Briefcase, Calendar, BarChart3,
  Trophy, Building2, ArrowLeftRight, CalendarDays, Zap,
  Mic, CheckCircle2
} from 'lucide-react';

// ─── IMAGE PLACEHOLDERS ───────────────────────────────────────────────────────
// Swap in ImageKit URLs when ready:
//   /ask-oz/ask-oz-hero.png
//   /ask-oz/ask-oz-avatar.png
//   /ask-oz/ask-oz-voice.png
const ASK_OZ_AVATAR_IMAGE = 'https://ik.imagekit.io/enrollforge/brands/studentsignal/Ask%20Oz/new%20oz%20Right.jpg';
const ASK_OZ_VOICE_IMAGE  = 'https://ik.imagekit.io/enrollforge/brands/studentsignal/Ask%20Oz/Oz%20Talk.jpg';

// ─── BRAND TOKENS ─────────────────────────────────────────────────────────────
const G     = '#1a5d3a';   // site primary green
const G2    = '#2E7D32';   // bright green headline
const GOLD  = '#f5a623';   // site gold
const PUR   = '#7B3FA0';   // purple accent
const PUR_L = '#f3e8ff';   // light purple bg
const SAG   = '#e8f5e9';   // light green bg
const _CREAM = '#f5f0e8';
const PAGE  = '#f8faf8';

// ─── DATA ─────────────────────────────────────────────────────────────────────
const PROMPT_CHIPS = [
  { icon: Calendar,       color: '#f5a623', text: 'When should I complete FAFSA?' },
  { icon: BarChart3,      color: '#1a5d3a', text: 'What schools are best for accounting?' },
  { icon: Trophy,         color: '#e53935', text: 'What schools are best for athletes?' },
  { icon: Search,         color: '#7B3FA0', text: 'How do I find scholarships?' },
  { icon: Building2,      color: '#0288d1', text: 'How do direct admissions work?' },
  { icon: ArrowLeftRight, color: '#f5a623', text: 'How do I compare two colleges?' },
  { icon: CalendarDays,   color: '#2E7D32', text: 'What should I do before senior year?' },
];

const CAPABILITIES = [
  { icon: Search,       color: '#1a5d3a', bg: '#e8f5e9', title: 'College Search Guidance',    desc: 'Find schools that match your goals, interests, location, and future plans.' },
  { icon: BookOpen,     color: '#7B3FA0', bg: PUR_L,     title: 'Scholarship Help',           desc: 'Understand scholarship options, deadlines, and ways to reduce college costs.' },
  { icon: DollarSign,   color: '#f5a623', bg: '#fff8e7', title: 'FAFSA & Financial Aid',      desc: 'Get plain-language help with FAFSA timing, grants, aid forms, and work-study.' },
  { icon: GraduationCap,color: '#0288d1', bg: '#e3f2fd', title: 'Major-Based Matching',       desc: 'Explore schools and pathways based on fields like accounting, business, healthcare, tech, and more.' },
  { icon: Trophy,       color: '#e53935', bg: '#fdecea', title: 'Student Athlete Guidance',   desc: 'Understand college options, recruiting timelines, and planning steps for athletes.' },
  { icon: ClipboardList,color: '#2E7D32', bg: '#e8f5e9', title: 'Admissions Planning',        desc: 'Get clearer next steps for applications, direct admissions, school comparison, and deadlines.' },
  { icon: PenLine,      color: '#e53935', bg: '#fdecea', title: 'Essay & Application Support',desc: 'Organize application tasks, essay ideas, and submission priorities.' },
  { icon: Briefcase,    color: '#f5a623', bg: '#fff8e7', title: 'Career Readiness',           desc: 'Connect college choices to majors, careers, skills, and long-term opportunity.' },
];

const INITIAL_MESSAGES = [
  {
    role: 'assistant',
    text: "Hi! I'm Ask Oz.\n\nI'm here to help you navigate college, scholarships, admissions, financial aid, and your future. What would you like to know?",
    time: 'Just now',
  },
];

// ─── SUBCOMPONENTS ────────────────────────────────────────────────────────────
const SectionTitle = ({ children, color = G2 }) => (
  <div className="flex items-center justify-center gap-2 mb-2">
    <span style={{ color, fontWeight: 700, fontSize: '0.85rem', letterSpacing: '0.06em' }}>+</span>
    <span className="font-bold text-sm tracking-widest uppercase" style={{ color }}>{children}</span>
    <span style={{ color, fontWeight: 700, fontSize: '0.85rem', letterSpacing: '0.06em' }}>+</span>
  </div>
);

const _WizardPlaceholder = ({ size = 320, className = '' }) => (
  <div
    className={`relative flex items-center justify-center ${className}`}
    style={{ width: size, height: size }}
    aria-hidden
  >
    {/* Outer glow ring — green */}
    <div className="absolute inset-0 rounded-full"
      style={{ background: 'radial-gradient(ellipse at 50% 50%, rgba(26,93,58,0.12) 0%, transparent 70%)' }} />
    {/* Mid ring — purple */}
    <div className="absolute rounded-full"
      style={{ inset: '12%', background: 'radial-gradient(ellipse at 50% 40%, rgba(123,63,160,0.10) 0%, transparent 65%)' }} />
    {/* Inner circle */}
    <div className="rounded-full flex items-center justify-center"
      style={{
        width: size * 0.62, height: size * 0.62,
        background: 'linear-gradient(145deg, #e8f5e9 0%, #f3e8ff 100%)',
        border: '2px solid rgba(26,93,58,0.15)',
        boxShadow: '0 8px 40px rgba(26,93,58,0.12), 0 0 0 8px rgba(26,93,58,0.04)',
      }}>
      <div className="text-center select-none">
        <div style={{ fontSize: size * 0.18, lineHeight: 1 }}>🧙</div>
        <div className="font-black mt-1" style={{ color: G, fontSize: size * 0.055 }}>ASK OZ</div>
      </div>
    </div>
    {/* Sparkles */}
    {[
      { top: '10%', left: '15%', size: 8,  color: G },
      { top: '22%', right: '10%', size: 6, color: PUR },
      { bottom: '18%', left: '10%', size: 7, color: GOLD },
      { bottom: '12%', right: '18%', size: 5, color: G2 },
      { top: '45%', left: '4%',  size: 5,  color: PUR },
    ].map((s, i) => (
      <div key={i} className="absolute rounded-full animate-pulse"
        style={{ width: s.size, height: s.size, background: s.color, opacity: 0.5, top: s.top, left: s.left, right: s.right, bottom: s.bottom }} />
    ))}
  </div>
);

const AvatarPlaceholder = ({ size = 52 }) => (
  <div className="rounded-full flex items-center justify-center flex-shrink-0"
    style={{
      width: size, height: size,
      background: 'linear-gradient(135deg, #e8f5e9 0%, #f3e8ff 100%)',
      border: `2px solid rgba(26,93,58,0.2)`,
      fontSize: size * 0.45,
    }}>
    🧙
  </div>
);

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function AskOzPage() {
  const [input, setInput]         = useState('');
  const [messages, setMessages]   = useState(INITIAL_MESSAGES);
  const [sending, setSending]     = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const [voiceFailed, setVoiceFailed]   = useState(false);
  const chatRef   = useRef(null);
  const inputRef  = useRef(null);

  const scrollToChat = () => chatRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const scrollToCaps = () => document.getElementById('capabilities')?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const handleChipClick = (text) => {
    setInput(text);
    scrollToChat();
    setTimeout(() => inputRef.current?.focus(), 400);
  };

  const clearConversation = () => setMessages(INITIAL_MESSAGES);

  const handleSend = useCallback(async (e) => {
    e?.preventDefault();
    const q = input.trim();
    if (!q || sending) return;
    setInput('');
    setSending(true);
    setMessages(prev => [...prev, { role: 'user', text: q, time: 'Just now' }]);
    // Chatbase not yet connected — show pending state
    await new Promise(r => setTimeout(r, 700));
    setMessages(prev => [...prev, {
      role: 'assistant',
      text: "Ask Oz is being connected. Please check back soon.",
      time: 'Just now',
      pending: true,
    }]);
    setSending(false);
  }, [input, sending]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  return (
    <div style={{ background: PAGE, minHeight: '100vh' }}>
      <TopExperienceLayer />

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* 1. HERO                                                             */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <section style={{ background: PAGE, paddingTop: 72, paddingBottom: 64 }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            {/* Left */}
            <div>
              <SectionTitle color={G}>ASK OZ</SectionTitle>
              <h1 className="font-black leading-tight mt-2 mb-4"
                style={{ fontSize: 'clamp(2.4rem, 5vw, 3.6rem)', color: '#111', letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                Your AI guide for<br />
                <span style={{ color: G2 }}>college, scholarships,</span><br />
                and what comes next.
              </h1>
              <p className="mb-8 text-base" style={{ color: '#555', maxWidth: 480, lineHeight: 1.7 }}>
                Ask Oz helps students make sense of college search, scholarships, FAFSA, admissions, majors, athlete pathways, and next-step planning — all inside Student Signal.
              </p>

              {/* CTAs */}
              <div className="flex flex-wrap gap-3 mb-8">
                <button
                  onClick={scrollToChat}
                  className="flex items-center gap-2 px-6 py-3 font-bold text-white text-sm rounded-full transition-all hover:opacity-90"
                  style={{ background: G, boxShadow: '0 4px 16px rgba(26,93,58,0.25)' }}
                >
                  <Zap size={16} />
                  Start Asking Oz
                </button>
                <button
                  onClick={scrollToCaps}
                  className="flex items-center gap-2 px-6 py-3 font-bold text-sm rounded-full transition-all hover:bg-green-50"
                  style={{ border: `2px solid ${G}`, color: G, background: 'white' }}
                >
                  See What Oz Can Help With
                </button>
              </div>

              {/* Trust strip */}
              <div className="flex flex-wrap gap-6">
                {[
                  { icon: Star,         label: 'Smart Guidance', sub: 'Real answers' },
                  { icon: Shield,       label: 'Trusted Info',   sub: 'Up-to-date' },
                  { icon: GraduationCap,label: 'Built for Students', sub: '100% focused on you' },
                ].map(({ icon: Icon, label, sub }) => (
                  <div key={label} className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: SAG }}>
                      <Icon size={16} style={{ color: G }} />
                    </div>
                    <div>
                      <div className="text-xs font-bold" style={{ color: '#111' }}>{label}</div>
                      <div className="text-xs" style={{ color: '#888' }}>{sub}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — Oz intro video */}
            <div className="order-first lg:order-last" style={{
              position: 'relative',
              width: '100%',
              maxWidth: '420px',
              margin: '0 auto'
            }}>
              <div style={{
                position: 'relative',
                paddingBottom: '100%',
                height: 0,
                overflow: 'hidden',
                borderRadius: '12px'
              }}>
                <iframe
                  src="https://api.vadoo.tv/iframe_test?id=LxEgC1dXYo0pJuu1fZ7DXuxSzCTwz0dR"
                  scrolling="no"
                  style={{
                    border: 0,
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    position: 'absolute',
                    overflow: 'hidden',
                    borderRadius: '12px'
                  }}
                  allowFullScreen={true}
                  allow="autoplay"
                  title="Meet Oz"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* 2. PROMPT CHIPS                                                     */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <section style={{ background: 'white', padding: '56px 0', borderTop: '1px solid #eee', borderBottom: '1px solid #eee' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionTitle color={G2}>What can I ask Oz?</SectionTitle>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 mt-8">
            {PROMPT_CHIPS.map(({ icon: Icon, color, text }) => (
              <button
                key={text}
                onClick={() => handleChipClick(text)}
                className="flex flex-col items-center text-center p-4 rounded-xl transition-all hover:shadow-md hover:-translate-y-0.5 active:translate-y-0"
                style={{
                  background: 'white', border: '1px solid #e8e8e8',
                  boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
                  cursor: 'pointer',
                }}
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center mb-3 flex-shrink-0"
                  style={{ background: color + '15' }}>
                  <Icon size={20} style={{ color }} />
                </div>
                <span className="text-xs font-medium leading-snug" style={{ color: '#333' }}>{text}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* 3. CHAT SECTION                                                     */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <section ref={chatRef} id="chat" style={{ background: PAGE, padding: '64px 0' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl overflow-hidden"
            style={{ background: 'white', border: '1px solid #e0e0e0', boxShadow: '0 4px 24px rgba(0,0,0,0.07)' }}>
            {/* Chat header */}
            <div className="flex items-start justify-between px-6 pt-6 pb-4"
              style={{ borderBottom: '1px solid #f0f0f0' }}>
              <div>
                <h2 className="text-xl font-black" style={{ color: '#111' }}>Ask Oz anything.</h2>
                <p className="text-sm mt-1" style={{ color: '#666', maxWidth: 380 }}>
                  Get help with college search, scholarships, admissions, financial aid, and planning your next move.
                </p>
              </div>
              <button onClick={clearConversation}
                className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-all hover:bg-gray-100 flex-shrink-0 ml-4"
                style={{ color: '#888', border: '1px solid #e0e0e0' }}>
                <RefreshCw size={12} />
                Clear conversation
              </button>
            </div>

            {/* Messages */}
            <div className="px-6 py-4 space-y-5" style={{ minHeight: 220, maxHeight: 420, overflowY: 'auto' }}>
              {messages.map((msg, i) => (
                <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                  {msg.role === 'assistant' && (
                    ASK_OZ_AVATAR_IMAGE && !avatarFailed
                      ? <img
                          src={ASK_OZ_AVATAR_IMAGE}
                          alt="Ask Oz"
                          onError={() => setAvatarFailed(true)}
                          className="rounded-full object-cover flex-shrink-0"
                          style={{ width: 44, height: 44 }}
                        />
                      : <AvatarPlaceholder size={44} />
                  )}
                  <div style={{ maxWidth: '75%' }}>
                    <div
                      className="rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-line"
                      style={msg.role === 'user'
                        ? { background: G, color: 'white', borderBottomRightRadius: 4 }
                        : { background: '#f7f7f7', color: '#111', borderBottomLeftRadius: 4, fontStyle: msg.pending ? 'italic' : 'normal', opacity: msg.pending ? 0.7 : 1 }
                      }
                    >
                      {msg.text}
                    </div>
                    <div className="text-xs mt-1 px-1" style={{ color: '#bbb', textAlign: msg.role === 'user' ? 'right' : 'left' }}>
                      {msg.time}
                    </div>
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex gap-3">
                  <AvatarPlaceholder size={44} />
                  <div className="flex items-center gap-1.5 px-4 py-3 rounded-2xl" style={{ background: '#f7f7f7', borderBottomLeftRadius: 4 }}>
                    {[0,1,2].map(i => (
                      <div key={i} className="w-2 h-2 rounded-full animate-bounce" style={{ background: G, animationDelay: `${i * 150}ms` }} />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="px-6 pb-4">
              <form onSubmit={handleSend} className="flex items-center gap-3">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type your question here..."
                  className="flex-1 px-4 py-3 text-sm rounded-full outline-none"
                  style={{ background: '#f5f5f5', border: '1px solid #e8e8e8', color: '#111' }}
                />
                <button
                  type="submit"
                  disabled={!input.trim() || sending}
                  className="w-11 h-11 rounded-full flex items-center justify-center transition-all hover:opacity-90 flex-shrink-0"
                  style={{ background: input.trim() ? G : '#ccc', cursor: input.trim() ? 'pointer' : 'not-allowed' }}
                >
                  <Send size={16} className="text-white" style={{ transform: 'translateX(1px)' }} />
                </button>
              </form>
              <p className="text-center text-xs mt-3" style={{ color: '#bbb' }}>
                Ask Oz can make mistakes. Always double-check important information.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* 4. CAPABILITY GRID                                                  */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <section id="capabilities" style={{ background: 'white', padding: '64px 0', borderTop: '1px solid #eee' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionTitle color={G2}>What Oz can help with</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
            {CAPABILITIES.map(({ icon: Icon, color, bg, title, desc }) => (
              <div key={title} className="rounded-xl p-5 transition-all hover:shadow-md"
                style={{ background: 'white', border: '1px solid #eee', boxShadow: '0 1px 6px rgba(0,0,0,0.05)' }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: bg }}>
                  <Icon size={20} style={{ color }} />
                </div>
                <div className="font-bold text-sm mb-1.5" style={{ color: '#111' }}>{title}</div>
                <p className="text-xs leading-relaxed" style={{ color: '#666' }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* 5. VOICE TEASER + TRUST                                             */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <section style={{ background: PAGE, padding: '64px 0' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            {/* Voice card */}
            <div className="rounded-2xl overflow-hidden flex gap-0"
              style={{ background: PUR_L, border: `1px solid rgba(123,63,160,0.15)`, boxShadow: '0 2px 12px rgba(123,63,160,0.08)' }}>
              {/* Image side */}
              <div className="flex-shrink-0 flex items-end justify-center" style={{ width: 130, paddingBottom: 0 }}>
                {ASK_OZ_VOICE_IMAGE && !voiceFailed ? (
                  <img
                    src={ASK_OZ_VOICE_IMAGE}
                    alt="Ask Oz Voice"
                    onError={() => setVoiceFailed(true)}
                    style={{ width: '100%', height: 200, objectFit: 'contain', objectPosition: 'bottom' }}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center w-full h-48">
                    <div className="rounded-full flex items-center justify-center"
                      style={{ width: 80, height: 80, background: 'rgba(123,63,160,0.12)', fontSize: '2.5rem' }}>
                      🧙
                    </div>
                    <div className="mt-2 w-8 h-8 rounded-full flex items-center justify-center"
                      style={{ background: PUR }}>
                      <Mic size={14} className="text-white" />
                    </div>
                  </div>
                )}
              </div>
              {/* Text side */}
              <div className="flex-1 p-6 flex flex-col justify-center">
                <h3 className="font-black text-xl leading-tight mb-3" style={{ color: PUR, letterSpacing: '-0.01em' }}>
                  Soon, you&apos;ll be able to talk with Oz.
                </h3>
                <p className="text-sm mb-5" style={{ color: '#555', lineHeight: 1.6 }}>
                  A future voice-powered Ask Oz experience will let you talk through questions in a more natural way.
                </p>
                <button
                  className="self-start px-5 py-2.5 text-sm font-bold rounded-full text-white transition-all hover:opacity-90"
                  style={{ background: PUR, cursor: 'default' }}
                  disabled
                >
                  Voice experience coming later
                </button>
              </div>
            </div>

            {/* Trust card */}
            <div className="rounded-2xl p-6"
              style={{ background: SAG, border: `1px solid rgba(26,93,58,0.15)`, boxShadow: '0 2px 12px rgba(26,93,58,0.06)' }}>
              <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4"
                style={{ background: 'rgba(26,93,58,0.12)' }}>
                <Shield size={24} style={{ color: G }} />
              </div>
              <h3 className="font-black text-xl mb-3" style={{ color: G2, letterSpacing: '-0.01em' }}>
                Guidance with signal. Not guesswork.
              </h3>
              <p className="text-sm mb-6" style={{ color: '#444', lineHeight: 1.7 }}>
                Ask Oz provides planning guidance to help students understand options and next steps. Always confirm admissions, financial aid, scholarship, and deadline details with official schools, scholarship providers, or financial aid offices.
              </p>
              <div className="space-y-3">
                {[
                  { icon: Star,         label: 'Student Focused',    sub: 'Always' },
                  { icon: Lock,         label: 'Privacy Protected',  sub: 'Your data matters' },
                  { icon: CheckCircle2, label: 'Trusted Sources',    sub: 'Verified information' },
                ].map(({ icon: Icon, label, sub }) => (
                  <div key={label} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: 'rgba(26,93,58,0.12)' }}>
                      <Icon size={14} style={{ color: G }} />
                    </div>
                    <div>
                      <span className="text-xs font-bold" style={{ color: '#111' }}>{label}</span>
                      <span className="text-xs ml-2" style={{ color: '#888' }}>{sub}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* 6. BRAND CLOSE                                                      */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <section style={{ background: 'white', padding: '40px 0', borderTop: '1px solid #eee' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link to="/" className="inline-block mb-2">
            <img src="/2026_logo.png" alt="StudentSignal" style={{ height: 44, width: 'auto' }} />
          </Link>
          <p className="text-sm" style={{ color: '#888' }}>Find the right path. Follow the signal.</p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
